/**
 * Runner View - Unified 2-Column Execution Interface
 * Standardized S3 Input -> Process -> S3 Output flow for all networks
 */
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { getRestApiBearerToken } from '@/services/apiToken';
import { motion } from 'framer-motion';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { useAppStore, useExecution } from '@/stores/useAppStore';
import FileDropzone from './inputs/FileDropzone';
import PayloadInput from './inputs/PayloadInput';
import ExecutionTraceViewer from './trace/ExecutionTraceViewer';
import FileList from './FileList';
import DataSectionHeader from './DataSectionHeader';
import SegmentedTabGroup from '../ui/SegmentedTabGroup';
import MountedContextPanel from '@stepflow/ui-shared/components/dataPlane/MountedContextPanel.jsx';
import { buildMemorySeed } from '@/utils/memorySyncUtils';
import {
  fetchMergedS3ExecutionLogs,
  parseS3LogSelectionFromOutputPrefix,
  parseTaskIdFromOutputPrefix,
} from '@/utils/runnerS3Logs';
import { normalizeLogLevel, initialRunLogLevel } from '@/constants/networkLogLevel';
import { capturePreRunSnapshot } from '@/services/networkRestoreApi';
import {
  listSecretsRegistry,
  secretsRegistryV1Enabled,
} from '@stepflow/ui-shared/services/toolsApi';
import { CODE_CONNECTOR_SOURCE_LABEL } from '@stepflow/ui-shared/utils/codeConnectorResolution';
import {
  IncludedTokensExceededError,
  throwForExecuteResponse,
} from '@stepflow/ui-shared/utils/billingErrors';
import {
  fetchOrgBillingEntitlement,
  fetchOrgUsageHistory,
} from '@stepflow/ui-shared/services/stripeBilling';
import UpgradeCard from '../UpgradeCard.jsx';
import { registrySecretVisibleForNetwork } from '@stepflow/ui-shared/components/secrets/SecretsCatalog.jsx';
import {
  VerticalResizeHandle,
  HorizontalResizeHandle,
  clamp
} from './RunnerPanelSplitters';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

const LS_RUNNER_SPLIT_TOP = 'stepflow.runner.splitTop';
const LS_RUNNER_SPLIT_LEFT = 'stepflow.runner.splitLeft';

function networkInterfaceUsesUniversalBuilder(iface) {
  try {
    return JSON.stringify(iface || {}).includes('logical:universal_builder');
  } catch {
    return false;
  }
}

function readSplit(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    if (v == null) return fallback;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

/** Build default payload { tenantId, prompt } for the Run form (optional customerId only via JSON). */
function inputContractToPayload() {
  return { tenantId: '', prompt: '' };
}

/**
 * Map an ExecutionRecord.status onto the StatusIndicator vocabulary the
 * ``Console`` / ``ExecutionTraceViewer`` widgets expect ('idle' | 'uploading'
 * | 'processing' | 'completed' | 'error'). RUNNING / AWAITING_APPROVAL ride
 * the 'processing' visual since both mean "SFN has not finished"; terminal
 * states split 'completed' vs 'error' on success/failure.
 */
function statusFromExecution(execStatus) {
  if (!execStatus) return null;
  if (execStatus === 'RUNNING' || execStatus === 'AWAITING_APPROVAL') return 'processing';
  if (execStatus === 'SUCCEEDED') return 'completed';
  if (execStatus === 'FAILED' || execStatus === 'TIMED_OUT' || execStatus === 'ABORTED') return 'error';
  return null;
}

export default function RunnerView({ network, interface: iface, runtimeConfig }) {
  const { user } = useAuth();
  const {
    runnerStatus,
    runnerLogs,
    setRunnerStatus,
    addRunnerLog,
    setRunnerOutput,
    appendTraceFromS3,
    clearRunnerLogs,
    upsertExecution,
  } = useAppStore();
  const [payloadValue, setPayloadValue] = useState({ tenantId: '', prompt: '' });
  const [executing, setExecuting] = useState(false);
  // PR 6e — blocked-by-budget surfaces the inline UpgradeCard above the
  // execution trace viewer when the runner returns 402 IncludedTokensExceeded.
  // Cleared on the next successful Run start.
  const [blockedByBudget, setBlockedByBudget] = useState(false);
  const [blockedPlanKey, setBlockedPlanKey] = useState(null);
  const [blockedUsageHistory, setBlockedUsageHistory] = useState([]);
  const [logLevel, setLogLevel] = useState(() => initialRunLogLevel(network));
  /** POST body.executionMode when HYDRATE (memory-first / hydrate path). */
  const [executionMode, setExecutionMode] = useState('');

  const usesUniversalBuilder = useMemo(
    () => networkInterfaceUsesUniversalBuilder(iface),
    [iface],
  );
  const [codeConnectorOptions, setCodeConnectorOptions] = useState([]);
  const [codeConnectorSecretId, setCodeConnectorSecretId] = useState('');
  /** Last POST /execute resolved provenance from ``codeConnectorResolution`` response. */
  const [connectorResolutionHint, setConnectorResolutionHint] = useState('');

  // Inherit saved network default when set; otherwise INFO. Resets when network or saved logLevel changes.
  useEffect(() => {
    setLogLevel(initialRunLogLevel(network));
  }, [network?.networkId, network?.logLevel]);
  const [lastTaskId, setLastTaskId] = useState(null);
  const [s3LogsLoading, setS3LogsLoading] = useState(false);
  const payloadRef = useRef(null);
  /**
   * Execution registry projection for the current run. This is the new
   * source of truth for the status-indicator widget: the local
   * ``runnerStatus`` field stays around for ephemeral UI ceremony
   * ('uploading' while staging files, 'idle' on mount), but any status that
   * depends on what the backend SFN is actually doing is derived from here.
   * Keeps the widget correct across tab switches, reconnects, and cross-tab
   * navigation — the slice persists; this component just reads it.
   */
  const currentExecution = useExecution(lastTaskId);
  const derivedRunnerStatus = statusFromExecution(currentExecution?.status);
  const effectiveRunnerStatus = derivedRunnerStatus || runnerStatus;
  /**
   * Live log streaming (AppSync Events) — *batch* semantics only.
   *
   * The backend publishes one ``LOG_BATCH`` envelope per ``S3BufferHandler``
   * flush (≈ one per Lambda invocation in the step-function chain, ≈ 10 per
   * run). The envelope's ``payload.logs`` is byte-for-byte the same array the
   * flush wrote to S3, so we feed it through ``appendTraceFromS3`` — the
   * existing dedupe-aware bulk importer. Consequence: live stream + the
   * manual "Merge S3" button converge to the same trace without double-
   * counting, no matter which arrives first.
   *
   * NetworkWorkbench owns the agent-channel subscription via
   * ``useAgentChannel`` and rebroadcasts deduped envelopes as ``window``
   * ``websocket-message`` CustomEvents (see ``workbenchRealtime.ts``).
   * RunnerView just filters for ``type === 'log_batch'`` / ``eventType === 'LOG_BATCH'``.
   *
   * Per-record streaming was tried and rejected — it emitted thousands of
   * near-duplicate envelopes per run and inflated AppSync publish spend ~100×.
   */
  const lastTaskIdRef = useRef(null);
  useEffect(() => {
    lastTaskIdRef.current = lastTaskId;
  }, [lastTaskId]);
  useEffect(() => {
    const onMsg = (ev) => {
      const d = ev?.detail;
      if (!d || typeof d !== 'object') return;
      const t = d.type || d.eventType;
      const isBatch =
        t === 'log_batch' || String(t || '').toUpperCase() === 'LOG_BATCH';
      if (!isBatch) return;
      const activeTaskId = lastTaskIdRef.current;
      if (activeTaskId && d.taskId && d.taskId !== activeTaskId) {
        // Batch belongs to a prior run — ignore so the current run's console
        // stays clean. (The "Merge S3" button can still pull historical tasks.)
        return;
      }
      const payload =
        d.payload && typeof d.payload === 'object' && !Array.isArray(d.payload)
          ? d.payload
          : {};
      const rows = Array.isArray(payload.logs) ? payload.logs : [];
      if (rows.length === 0) return;
      appendTraceFromS3(rows);
    };
    window.addEventListener('websocket-message', onMsg);
    return () => window.removeEventListener('websocket-message', onMsg);
  }, [appendTraceFromS3]);

  useEffect(() => {
    if (
      !secretsRegistryV1Enabled() ||
      !network?.networkId ||
      !usesUniversalBuilder
    ) {
      setCodeConnectorOptions([]);
      setCodeConnectorSecretId('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const nid = network.networkId;
        // Pass networkId so the backend returns this network's inheritance chain
        // (TENANT + matching NETWORK). The unfiltered tenant surface now excludes
        // NETWORK rows (down-only), so omitting it would hide a network-scoped
        // code-connector from the runner's picker.
        const { secrets } = await listSecretsRegistry({
          toolName: 'code-connector',
          networkId: nid && nid !== 'new' ? nid : undefined,
        });
        const rows = (Array.isArray(secrets) ? secrets : []).filter((r) => {
          if (String(r.toolName || '') !== 'code-connector') return false;
          if (String(r.testStatus || '').toUpperCase() !== 'TESTED_OK')
            return false;
          return registrySecretVisibleForNetwork(r, nid);
        });
        if (!cancelled) setCodeConnectorOptions(rows);
      } catch {
        if (!cancelled) setCodeConnectorOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [network?.networkId, usesUniversalBuilder]);

  useEffect(() => {
    if (!network?.networkId) return;
    setPayloadValue(inputContractToPayload());
    setExecutionMode('');
    setCodeConnectorSecretId('');
    setConnectorResolutionHint('');
  }, [iface, network?.networkId]);
  const [inputCurrentPrefix, setInputCurrentPrefix] = useState('');
  const [outputCurrentPrefix, setOutputCurrentPrefix] = useState('');
  // Default-active is ``mounted`` so the first thing users see in the Run
  // tab is the read-only Data Plane rollup ("what context will agents see?")
  // — the legacy folder-tree sit under the Input tab for power users.
  const [dataTab, setDataTab] = useState('mounted');
  const inputFileListRef = useRef(null);
  const outputFileListRef = useRef(null);

  /** Top row vs trace: default ~1.1 : 0.9 → 0.55 */
  const [topFrac, setTopFrac] = useState(() => clamp(readSplit(LS_RUNNER_SPLIT_TOP, 0.55), 0.22, 0.78));
  /** Payload | Data: default 2fr : 3fr → 0.4 */
  const [leftFrac, setLeftFrac] = useState(() => clamp(readSplit(LS_RUNNER_SPLIT_LEFT, 0.4), 0.22, 0.78));
  const topDragStart = useRef(0.55);
  const leftDragStart = useRef(0.4);

  useEffect(() => {
    try {
      localStorage.setItem(LS_RUNNER_SPLIT_TOP, String(topFrac));
    } catch {
      /* ignore */
    }
  }, [topFrac]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_RUNNER_SPLIT_LEFT, String(leftFrac));
    } catch {
      /* ignore */
    }
  }, [leftFrac]);

  const memorySeed = useMemo(() => buildMemorySeed(network?.agents), [network?.agents]);

  /** S3 merge: selected execution folder under Data → Output → logs/(poolId/)taskId/…, else last successful execute */
  const s3MergeSelection = useMemo(() => {
    const fromOutputBrowse = parseS3LogSelectionFromOutputPrefix(outputCurrentPrefix);
    if (fromOutputBrowse) return fromOutputBrowse;
    return lastTaskId ? { taskId: lastTaskId, prefix: `logs/${lastTaskId}/`, poolId: null } : null;
  }, [outputCurrentPrefix, lastTaskId]);
  const s3MergeTaskId = s3MergeSelection?.taskId ?? null;

  const activeDataType = dataTab === 'input' ? 'input' : 'output';
  const activeFileListRef = dataTab === 'input' ? inputFileListRef : outputFileListRef;
  const activeCurrentPrefix = dataTab === 'input' ? inputCurrentPrefix : outputCurrentPrefix;
  // The Data Plane rollup has its own header actions; suppress the legacy
  // input/output icon row when it's active so the chrome doesn't double up.
  const showLegacyDataActions = dataTab === 'input' || dataTab === 'output';

  const handleExecute = async () => {
    const payload = payloadRef.current?.getPayload?.();
    if (!payload) {
      addRunnerLog('❌ Invalid JSON. Fix the payload before executing.');
      return;
    }

    setExecuting(true);
    setRunnerStatus('processing');
    // Clear any prior 402-blocked banner — the user just clicked Run
    // again, so we should give the next attempt a clean surface
    // (the 402 may have been resolved by an in-tab upgrade, or the
    // user might be intentionally trying again to confirm).
    setBlockedByBudget(false);
    setBlockedPlanKey(null);
    setBlockedUsageHistory([]);
    addRunnerLog('📝 Executing network with JSON input');

    try {
      const parsedInput = payload;
      const idToken = getRestApiBearerToken();

      addRunnerLog('🚀 Starting Step Function...');

      const input = { ...parsedInput };
      if (!String(input.customerId ?? '').trim()) {
        delete input.customerId;
      }
      if (!String(input.tenantId ?? '').trim()) {
        delete input.tenantId;
      }
      // ``connectionId`` is retained on the wire as ``null`` purely so older
      // REST handlers that read the field don't trip on absence. Live events
      // now stream via AppSync Events (``useAgentChannel``).
      const requestBody = { input, connectionId: null, logLevel };
      const cc = String(codeConnectorSecretId || '').trim();
      if (cc) {
        requestBody.codeConnectorSecretId = cc;
      }
      if (executionMode === 'HYDRATE') {
        requestBody.executionMode = 'HYDRATE';
      }
      const staged = payloadRef.current?.getTestAttachments?.() ?? [];
      const attachmentStagingId = payloadRef.current?.getAttachmentStagingId?.() ?? '';
      if (attachmentStagingId) {
        requestBody.attachmentStagingId = attachmentStagingId;
      }
      if (staged.length > 0) {
        const needsAsync = staged.some((a) => a.needs_processing);
        requestBody.attachmentInput = {
          needs_async_processing: needsAsync,
          attachments: staged.map((a) => ({
            filename: a.filename,
            contentType: a.contentType,
            bucket: a.bucket,
            s3Key: a.s3Key,
            size: a.size,
            needs_processing: a.needs_processing
          }))
        };
        addRunnerLog(`📎 Including ${staged.length} attachment(s) with execution`);
      }
      console.log('[StepFlow][ConnectionId] execute: POST /execute body.connectionId', {
        isNull: requestBody.connectionId == null,
        type: typeof requestBody.connectionId,
        length: requestBody.connectionId != null ? String(requestBody.connectionId).length : 0
      });

      // Auto-snapshot before every Run-tab execution. The backend dedupes
      // by content-hash so repeat runs against unchanged state produce at
      // most one row. This is strictly best-effort — any failure (403,
      // network blip, etc.) must not block the run.
      try {
        if (network?.networkId) {
          await capturePreRunSnapshot(network.networkId);
        }
      } catch (snapErr) {
        console.warn('[StepFlow] pre-run snapshot skipped', snapErr);
      }

      const response = await fetch(`${API_BASE_URL}/networks/${network.networkId}/execute`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throwForExecuteResponse(response.status, errBody);
      }

      const result = await response.json();
      const ccRes =
        result && typeof result.codeConnectorResolution === 'object'
          ? result.codeConnectorResolution
          : null;
      if (
        ccRes &&
        CODE_CONNECTOR_SOURCE_LABEL[ccRes.source] !== undefined &&
        usesUniversalBuilder
      ) {
        const srcLbl = CODE_CONNECTOR_SOURCE_LABEL[ccRes.source] || ccRes.source;
        const sid = String(ccRes.secretId || '').trim();
        const idTail =
          ccRes.source !== 'none' && sid.length > 0 ? ` (${sid.slice(-12)})` : '';
        setConnectorResolutionHint(`${srcLbl}${idTail}`);
        addRunnerLog(`🔗 Connector resolved: ${srcLbl}${idTail || ''}`);
      } else if (usesUniversalBuilder) {
        setConnectorResolutionHint('');
      }
      addRunnerLog('✅ Execution started successfully');
      addRunnerLog(`🔗 Execution ARN: ${result.executionArn}`);
      if (result.taskId) {
        setLastTaskId(result.taskId);
        const runPoolId = String(input.tenantId || requestBody.poolId || requestBody.networkTenantId || 'default').trim() || 'default';
        addRunnerLog(
          `📋 Run ID: ${result.taskId} (same as Step Functions execution name; logs: output/logs/${runPoolId}/${result.taskId}/)`
        );
        // Optimistic seed into the execution registry. Widgets reading
        // ``useExecution(taskId)`` now see RUNNING the instant ``/execute``
        // returns, and stay RUNNING until the SFN bridge fans in the real
        // terminal event over AppSync. No more "fake completed" flip the
        // moment the POST returns.
        upsertExecution({
          taskId: result.taskId,
          networkId: network.networkId,
          poolId: runPoolId,
          executionArn: result.executionArn,
          status: 'RUNNING',
          startedAt: result.startDate || new Date().toISOString(),
          startedBy: user?.sub,
          source: 'optimistic',
        });
      }

      payloadRef.current?.rotateStagingAfterSuccessfulRun?.();

      setRunnerOutput({
        executionArn: result.executionArn,
        startDate: result.startDate,
        status: 'RUNNING',
        taskId: result.taskId
      });
      // Historic behaviour flipped ``runnerStatus`` to ``'completed'`` here,
      // but the SFN is only *started* at this point — it's almost never done.
      // The widget now derives its visual status from
      // ``useExecution(lastTaskId)`` (see ``effectiveRunnerStatus``), so we
      // clear the local field back to idle and let the registry drive truth.
      setRunnerStatus('idle');
    } catch (error) {
      if (error instanceof IncludedTokensExceededError) {
        addRunnerLog(
          '🛑 Run blocked: this organization has used all included tokens for this billing period. Upgrade your plan or wait for renewal to keep running.',
        );
        // Resolve current plan key so the inline UpgradeCard can suggest
        // the right next tier. Best-effort fetch — if it fails the
        // ``UpgradeCard`` falls back to the lowest finite tier suggestion
        // (no plan key resolved → Starter), which is still actionable.
        try {
          const ent = await fetchOrgBillingEntitlement();
          setBlockedPlanKey(ent?.planKey || null);
        } catch {
          setBlockedPlanKey(null);
        }
        // PR 6g — usage history feeds the dollar-impact preview on the
        // inline UpgradeCard. Best-effort; the card renders cleanly
        // with empty history (falls back to plan-spec copy).
        try {
          const hist = await fetchOrgUsageHistory({ months: 3 });
          setBlockedUsageHistory(Array.isArray(hist?.months) ? hist.months : []);
        } catch {
          setBlockedUsageHistory([]);
        }
        setBlockedByBudget(true);
      } else {
        addRunnerLog(`❌ Execution failed: ${error.message}`);
      }
      setRunnerStatus('error');
    } finally {
      setExecuting(false);
    }
  };

  const handleLoadS3Logs = useCallback(async () => {
    const taskId = s3MergeTaskId;
    if (!taskId || !network?.networkId) return;
    const idToken = getRestApiBearerToken();
    setS3LogsLoading(true);
    addRunnerLog(`📂 Loading buffered logs from S3 for task ${taskId}...`);
    try {
      const rows = await fetchMergedS3ExecutionLogs({
        apiBaseUrl: API_BASE_URL,
        networkId: network.networkId,
        taskId,
        prefix: s3MergeSelection?.prefix,
        idToken
      });
      if (rows.length === 0) {
        addRunnerLog('ℹ️ No S3 log files yet (Lambdas flush at end of invocation). Try again after the run progresses.');
        return;
      }
      appendTraceFromS3(rows);
      addRunnerLog(`── Merged ${rows.length} structured event(s) from S3 ──`);
    } catch (e) {
      addRunnerLog(`❌ S3 log merge failed: ${e.message}`);
    } finally {
      setS3LogsLoading(false);
    }
  }, [s3MergeTaskId, s3MergeSelection?.prefix, network?.networkId, user?.idToken, addRunnerLog, appendTraceFromS3]);

  const handleUploadSuccess = () => {
    inputFileListRef.current?.refresh?.();
  };

  const handleFormat = () => {
    payloadRef.current?.format?.();
  };

  return (
    <div data-runner-root className="h-full flex flex-col min-h-0 bg-gray-50/50">
      {/* Top row: Network Payload | vertical grip | Data */}
      <div
        data-runner-top-row
        className="flex flex-row min-h-0 w-full overflow-hidden pl-6 pr-6 pt-6 pb-3 gap-0"
        style={{ flex: `${topFrac} 1 0`, minHeight: '160px' }}
      >
        {/* Widget 1: Network Payload */}
        <div
          className="min-h-0 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col"
          style={{ flex: `${leftFrac} 1 0`, minWidth: '200px' }}
        >
          <div className="flex-shrink-0 px-5 py-3 border-b border-gray-200 bg-gray-100 flex flex-nowrap items-center justify-between gap-3 min-h-[44px]">
            <h3 className="text-sm font-semibold text-gray-700 shrink-0">Network Payload</h3>
            <div className="flex flex-nowrap items-center justify-end gap-2 shrink-0 min-w-0">
              <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap shrink-0">
                <span className="hidden sm:inline">Mode</span>
                <select
                  value={executionMode}
                  onChange={(e) => setExecutionMode(e.target.value)}
                  disabled={executing}
                  className="max-w-[9rem] border border-gray-300 rounded-md px-1.5 py-1 text-xs bg-white text-gray-800 disabled:opacity-50"
                  aria-label="Execution mode"
                >
                  <option value="">Default</option>
                  <option value="HYDRATE">HYDRATE</option>
                </select>
              </label>
              {usesUniversalBuilder &&
              secretsRegistryV1Enabled() &&
              codeConnectorOptions.length > 0 ? (
                <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap shrink-0 min-w-0 max-w-[12rem] sm:max-w-[14rem]">
                  <span
                    className="hidden sm:inline truncate"
                    title="Git connection (runtime — swap repo/credentials per execution)"
                  >
                    Git
                  </span>
                  {connectorResolutionHint ? (
                    <span
                      className="hidden xl:inline truncate max-w-[8rem] text-[10px] font-normal normal-case text-gray-500"
                      title={`Last resolved: ${connectorResolutionHint}`}
                    >
                      {connectorResolutionHint}
                    </span>
                  ) : null}
                  <select
                    value={codeConnectorSecretId}
                    onChange={(e) => setCodeConnectorSecretId(e.target.value)}
                    disabled={executing}
                    className="min-w-0 flex-1 max-w-[10rem] sm:max-w-[12rem] border border-gray-300 rounded-md px-1.5 py-1 text-xs bg-white text-gray-800 disabled:opacity-50 truncate"
                    aria-label="Universal Builder runtime git connector (registry code-connector secret)"
                    title={
                      codeConnectorSecretId
                        ? String(codeConnectorSecretId)
                        : 'Pick a TESTED_OK code-connector — the plan stays repo/credential agnostic; this only changes which connector is mounted at run time'
                    }
                  >
                    <option value="">Connector (network default)</option>
                    {codeConnectorOptions.map((row) => {
                      const sid = String(row.secretId || '').trim();
                      const lbl =
                        row.displayName ?? row.name ?? row.label ?? sid;
                      const st = lbl || sid || 'connector';
                      return (
                        <option key={sid || st} value={sid}>
                          {String(st)}
                        </option>
                      );
                    })}
                  </select>
                </label>
              ) : null}
              <button
                type="button"
                onClick={handleFormat}
                className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              >
                Format
              </button>
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleExecute}
                disabled={executing}
                className="px-2.5 py-1 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 whitespace-nowrap"
              >
                {executing ? (
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                Execute Network
              </motion.button>
            </div>
          </div>
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <PayloadInput
              ref={payloadRef}
              attachmentFooterPlacement="below-content"
              value={payloadValue}
              onChange={setPayloadValue}
              disabled={executing}
              logLevel={logLevel}
              onLogLevelChange={setLogLevel}
              networkId={network.networkId}
              runtimeConfig={runtimeConfig}
              networkSavedLogLevel={
                network?.logLevel != null && String(network.logLevel).trim() !== ''
                  ? normalizeLogLevel(network.logLevel)
                  : null
              }
            />
          </div>
        </div>

        <VerticalResizeHandle
          onDragBegin={() => {
            leftDragStart.current = leftFrac;
          }}
          onDrag={(d) => setLeftFrac(clamp(leftDragStart.current + d, 0.22, 0.78))}
        />

        {/* Widget 2: Data — single header: title + tabs | icon actions */}
        <div
          className="min-h-0 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col"
          style={{ flex: `${1 - leftFrac} 1 0`, minWidth: '240px' }}
        >
          <div className="flex-shrink-0 border-b border-gray-200 bg-gray-100 px-3 py-2">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-700">Data</h3>
                <SegmentedTabGroup
                  value={dataTab}
                  onChange={setDataTab}
                  options={[
                    { value: 'mounted', label: 'Mounted' },
                    { value: 'input', label: 'Input' },
                    { value: 'output', label: 'Output' }
                  ]}
                  ariaLabel="Mounted context, raw input or output files"
                />
              </div>
              {showLegacyDataActions ? (
                <DataSectionHeader
                  key={dataTab}
                  variant="icons"
                  embedded
                  hideLabel
                  type={activeDataType}
                  networkId={network.networkId}
                  currentPrefix={activeCurrentPrefix}
                  fileListRef={activeFileListRef}
                  onRefresh={() => activeFileListRef.current?.refresh?.()}
                />
              ) : null}
            </div>
          </div>
          <div className="flex-1 min-h-0 relative">
            {/* Default-active 'Mounted' tab — read-only rollup of every Data
                Plane asset visible to this network. We keep the panel mounted
                (display:none style toggle via class) so the user can flick
                between tabs without losing scroll/refresh state. */}
            <div
              className={`absolute inset-0 flex flex-col min-h-0 overflow-hidden ${
                dataTab !== 'mounted' ? 'invisible pointer-events-none z-0' : 'z-10'
              }`}
              aria-hidden={dataTab !== 'mounted'}
            >
              <MountedContextPanel
                key={network.networkId}
                networkId={network.networkId}
                refreshSeed={memorySeed}
                onManageInDataPlane={() => {
                  if (typeof window !== 'undefined') {
                    window.dispatchEvent(
                      new CustomEvent('stepflow:open-data-plane-tab', {
                        detail: { networkId: network.networkId },
                      }),
                    );
                  }
                }}
              />
            </div>
            <div
              className={`absolute inset-0 flex flex-col min-h-0 overflow-hidden ${
                dataTab !== 'input' ? 'invisible pointer-events-none z-0' : 'z-10'
              }`}
              aria-hidden={dataTab !== 'input'}
            >
              <div className="flex-1 min-h-0 overflow-auto">
                <FileList
                  key={network.networkId}
                  ref={inputFileListRef}
                  networkId={network.networkId}
                  type="input"
                  hideHeader
                  enableMemorySync
                  memorySeed={memorySeed}
                  onPrefixChange={setInputCurrentPrefix}
                />
              </div>
              <div className="flex-shrink-0 p-3 border-t border-gray-100">
                <FileDropzone
                  networkId={network.networkId}
                  runtimeConfig={runtimeConfig}
                  onUploadSuccess={handleUploadSuccess}
                  compact
                  currentPrefix={inputCurrentPrefix}
                />
              </div>
            </div>
            <div
              className={`absolute inset-0 flex flex-col min-h-0 overflow-hidden ${
                dataTab !== 'output' ? 'invisible pointer-events-none z-0' : 'z-10'
              }`}
              aria-hidden={dataTab !== 'output'}
            >
              <div className="flex-1 min-h-0 overflow-auto">
                <FileList
                  key={network.networkId}
                  ref={outputFileListRef}
                  networkId={network.networkId}
                  type="output"
                  hideHeader
                  onPrefixChange={setOutputCurrentPrefix}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <HorizontalResizeHandle
        onDragBegin={() => {
          topDragStart.current = topFrac;
        }}
        onDrag={(d) => setTopFrac(clamp(topDragStart.current + d, 0.22, 0.78))}
      />

      {/* Bottom row: Execution trace viewer (timeline + inspector); top Network Payload | Data unchanged */}
      <div
        className="min-h-0 px-6 pb-6 overflow-hidden flex flex-col gap-3"
        style={{ flex: `${1 - topFrac} 1 0`, minHeight: '140px' }}
      >
        {blockedByBudget ? (
          <div data-testid="runner-blocked-by-budget">
            <UpgradeCard
              currentPlanKey={blockedPlanKey}
              tone="blocked"
              successPath={window.location.pathname}
              cancelPath={window.location.pathname}
              usageHistory={blockedUsageHistory}
            />
          </div>
        ) : null}
        <div className="h-full rounded-xl border border-gray-200 shadow-sm overflow-hidden bg-white">
          <ExecutionTraceViewer
            status={effectiveRunnerStatus}
            logs={runnerLogs}
            mergeTaskId={s3MergeTaskId}
            mergeFromOutputFolder={Boolean(parseTaskIdFromOutputPrefix(outputCurrentPrefix))}
            onMergeS3={s3MergeTaskId ? handleLoadS3Logs : undefined}
            s3Loading={s3LogsLoading}
            onClearLogs={() => clearRunnerLogs()}
          />
        </div>
      </div>
    </div>
  );
}
